import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Minus,
  Plus,
  ShoppingBag,
  Trash2,
  MessageCircle,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatNaira, VAT_RATE } from "@/data/menu";
import { RESTAURANT, waLink } from "@/data/restaurant";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "meritz_subscriber_v1";

// Build pickup/delivery time slots: ASAP + every 30 min for next 6 hours
function buildTimeSlots() {
  const slots = [{ value: "asap", label: "ASAP (as soon as possible)" }];
  const now = new Date();
  // round up to next 15
  const m = now.getMinutes();
  now.setMinutes(m + (15 - (m % 15)));
  now.setSeconds(0);
  for (let i = 0; i < 16; i++) {
    const t = new Date(now.getTime() + i * 30 * 60 * 1000);
    const hh = t.getHours();
    const mm = String(t.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    const label = `Today, ${h12}:${mm} ${ampm}`;
    slots.push({ value: t.toISOString(), label });
  }
  return slots;
}

export default function CartDrawer() {
  const {
    items,
    updateQty,
    removeItem,
    clearCart,
    totals,
    drawerOpen,
    closeCart,
  } = useCart();

  const slots = useMemo(() => buildTimeSlots(), [drawerOpen]);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    orderType: "pickup", // 'pickup' | 'delivery'
    address: "",
    time: "asap",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isEmpty = items.length === 0;

  const buildWhatsAppMessage = () => {
    const lines = [];
    lines.push("🍽️ *NEW ORDER — Meritz Restaurant*");
    lines.push("");
    lines.push(`*Name:* ${form.name}`);
    lines.push(`*Phone:* ${form.phone}`);
    lines.push(
      `*Order type:* ${form.orderType === "delivery" ? "Delivery" : "Pickup"}`,
    );
    if (form.orderType === "delivery" && form.address.trim()) {
      lines.push(`*Delivery address:* ${form.address.trim()}`);
    }
    const slotLabel =
      slots.find((s) => s.value === form.time)?.label || form.time;
    lines.push(`*Wanted time:* ${slotLabel}`);
    if (form.note.trim()) lines.push(`*Note:* ${form.note.trim()}`);
    lines.push("");
    lines.push("*ORDER:*");
    items.forEach((it) => {
      lines.push(
        `• ${it.name} × ${it.qty} — ${formatNaira(it.price * it.qty)}`,
      );
    });
    lines.push("");
    lines.push(`Subtotal: ${formatNaira(totals.subtotal)}`);
    lines.push(
      `VAT & Service (${Math.round(VAT_RATE * 100)}%): ${formatNaira(totals.vat)}`,
    );
    lines.push(`*TOTAL: ${formatNaira(totals.total)}*`);
    if (form.orderType === "delivery") {
      lines.push("");
      lines.push(
        `_Delivery fee (₦${RESTAURANT.delivery.feeMin.toLocaleString()}–₦${RESTAURANT.delivery.feeMax.toLocaleString()}) will be confirmed based on location._`,
      );
    }
    return lines.join("\n");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmpty) {
      toast.error("Your cart is empty");
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    if (form.phone.trim().length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (form.orderType === "delivery" && form.address.trim().length < 5) {
      toast.error("Please enter your delivery address");
      return;
    }

    setSubmitting(true);
    try {
      // Save lead to backend (don't block on failure)
      try {
        await axios.post(`${API}/subscribers`, {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: "",
          consent: true,
          source: "cart_checkout",
        });
        try {
          localStorage.setItem(LS_KEY, "1");
        } catch {}
      } catch {}

      const msg = buildWhatsAppMessage();
      const url = waLink(msg);
      toast.success("Order ready — opening WhatsApp…");

      // Open WhatsApp
      setTimeout(() => {
        try {
          const w = window.open(url, "_blank");
          if (w) {
            try {
              w.opener = null;
            } catch {}
          } else {
            window.location.href = url;
          }
        } catch {
          window.location.href = url;
        }
      }, 250);

      // Clear cart after successful handoff
      setTimeout(() => {
        clearCart();
        closeCart();
        setForm({
          name: "",
          phone: "",
          orderType: "pickup",
          address: "",
          time: "asap",
          note: "",
        });
      }, 600);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={(o) => !o && closeCart()}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-[#FFFCF5] border-l border-[#E8E2D6] p-0 flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E8E2D6]">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#C73E3A] text-white grid place-items-center">
              <ShoppingBag size={18} />
            </span>
            <div className="text-left">
              <SheetTitle className="font-display text-2xl font-extrabold tracking-tight">
                Your Cart
              </SheetTitle>
              <SheetDescription className="text-[#6B635A] text-sm">
                {totals.count > 0
                  ? `${totals.count} item${totals.count > 1 ? "s" : ""} — ${formatNaira(totals.total)}`
                  : "Tap any menu item to add it here."}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isEmpty ? (
            <div
              data-testid="cart-empty"
              className="h-full flex flex-col items-center justify-center text-center py-16"
            >
              <div className="w-16 h-16 rounded-full bg-[#F2B441]/20 grid place-items-center mb-3">
                <ShoppingBag size={28} className="text-[#C73E3A]" />
              </div>
              <p className="font-display font-bold text-lg text-[#2C2825]">
                Cart is empty
              </p>
              <p className="text-sm text-[#6B635A] mt-1 max-w-[240px]">
                Tap any item on the menu to view details & add it here.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="cart-items-list">
              {items.map((it) => (
                <li
                  key={it.name}
                  data-testid={`cart-item-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="bg-white border border-[#E8E2D6] rounded-2xl p-3 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-[#2C2825] leading-tight">
                      {it.name}
                    </p>
                    <p className="text-xs text-[#6B635A] mt-0.5">
                      {formatNaira(it.price)} each
                    </p>
                    <div className="mt-2 inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateQty(it.name, it.qty - 1)}
                        data-testid={`cart-qty-minus-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="w-7 h-7 rounded-full border border-[#E8E2D6] bg-white grid place-items-center hover:bg-[#F9F6F0]"
                        aria-label="Decrease"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="font-bold text-sm w-6 text-center">
                        {it.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQty(it.name, it.qty + 1)}
                        data-testid={`cart-qty-plus-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="w-7 h-7 rounded-full border border-[#E8E2D6] bg-white grid place-items-center hover:bg-[#F9F6F0]"
                        aria-label="Increase"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2 shrink-0">
                    <span className="font-display font-bold text-[#C73E3A]">
                      {formatNaira(it.price * it.qty)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeItem(it.name)}
                      data-testid={`cart-remove-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-[#6B635A] hover:text-[#C73E3A]"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Checkout form */}
          {!isEmpty && (
            <form
              id="cart-checkout-form"
              onSubmit={handleSubmit}
              noValidate
              className="mt-6 space-y-3"
              data-testid="cart-checkout-form"
            >
              <h3 className="font-display font-bold text-lg text-[#2C2825]">
                Your details
              </h3>
              <Field label="Full name">
                <input
                  data-testid="cart-name"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. Chioma O."
                  className={inputClass}
                />
              </Field>
              <Field label="Phone (WhatsApp)">
                <input
                  data-testid="cart-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="+234 ..."
                  className={inputClass}
                />
              </Field>

              <Field label="Order type">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "pickup", l: "Pickup" },
                    { v: "delivery", l: "Delivery" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setField("orderType", opt.v)}
                      data-testid={`cart-ordertype-${opt.v}`}
                      className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                        form.orderType === opt.v
                          ? "bg-[#2C2825] text-white border-[#2C2825]"
                          : "bg-white text-[#2C2825] border-[#E8E2D6]"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </Field>

              {form.orderType === "delivery" && (
                <Field label="Delivery address">
                  <textarea
                    data-testid="cart-address"
                    value={form.address}
                    onChange={(e) => setField("address", e.target.value)}
                    placeholder="House no, street, area, landmark…"
                    rows={2}
                    className={inputClass + " py-2 resize-none"}
                  />
                  <p className="text-[11px] text-[#6B635A] mt-1.5">
                    Delivery fee {formatNaira(RESTAURANT.delivery.feeMin)}–
                    {formatNaira(RESTAURANT.delivery.feeMax)} confirmed on
                    WhatsApp.
                  </p>
                </Field>
              )}

              <Field
                label={
                  form.orderType === "delivery"
                    ? "When should we deliver?"
                    : "When will you pick up?"
                }
              >
                <select
                  data-testid="cart-time"
                  value={form.time}
                  onChange={(e) => setField("time", e.target.value)}
                  className={inputClass}
                >
                  {slots.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Special note (optional)">
                <textarea
                  data-testid="cart-note"
                  value={form.note}
                  onChange={(e) => setField("note", e.target.value)}
                  placeholder="e.g. less pepper, extra plantain…"
                  rows={2}
                  className={inputClass + " py-2 resize-none"}
                />
              </Field>
            </form>
          )}
        </div>

        {/* Sticky footer with totals + submit */}
        {!isEmpty && (
          <div className="border-t border-[#E8E2D6] bg-white px-5 py-4 space-y-3 shrink-0">
            <div
              data-testid="cart-totals"
              className="space-y-1.5 text-sm text-[#2C2825]"
            >
              <div className="flex justify-between">
                <span className="text-[#6B635A]">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-semibold">
                  {formatNaira(totals.subtotal)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B635A]">
                  VAT & service ({Math.round(VAT_RATE * 100)}%)
                </span>
                <span data-testid="cart-vat" className="font-semibold">
                  {formatNaira(totals.vat)}
                </span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-[#E8E2D6]">
                <span className="font-display font-bold text-base">Total</span>
                <span
                  data-testid="cart-total"
                  className="font-display font-extrabold text-lg text-[#C73E3A]"
                >
                  {formatNaira(totals.total)}
                </span>
              </div>
            </div>

            <button
              type="submit"
              form="cart-checkout-form"
              disabled={submitting}
              data-testid="cart-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb854] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors"
            >
              {submitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <MessageCircle size={16} />
              )}
              {submitting ? "Preparing…" : "Send Order via WhatsApp"}
            </button>
            <button
              type="button"
              onClick={() => {
                clearCart();
                toast.success("Cart cleared");
              }}
              data-testid="cart-clear"
              className="w-full text-xs font-semibold text-[#6B635A] hover:text-[#C73E3A] py-1"
            >
              Clear cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const inputClass =
  "w-full h-11 px-3.5 rounded-xl border border-[#E8E2D6] bg-white font-medium text-[#2C2825] placeholder:text-[#6B635A]/70 focus:outline-none focus:border-[#C73E3A] transition-colors text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold tracking-wider uppercase text-[#6B635A] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
